import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { NotificationManager } from "react-notifications";
import Modal from "../../../components/Modal/Modal";
import Select from "react-select";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";
import { Radio, RadioGroup, Stack } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const AddToFarmClubModal = ({ showModal, onClose, contactId }) => {
  const [config] = useAuth();
  const selectRef = useRef(null);
  const navigate = useNavigate();
  const [bdsOptions, setBdsOptions] = useState([]);
  const [transactionOptions, setTransactionOptions] = useState([]);
  const [linkOptions, setLinkOptions] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    date_added: "",
    associate: "",
    transaction_type: "",
    lead_type: "",
    portfolio: 0,
    link: "",
  });

  const fetchUsers = (listType) => {
    let url = `${BASE_URL}/get-datalisting?list=${listType}`;
    axios
      .get(url, config)
      .then((res) => {
        const data = res?.data?.data;
        const userOptions =
          data?.users?.map((el) => ({
            value: el?.id,
            label: `${el?.first_name} ${el?.last_name}`,
            role: el?.role_id,
          })) || [];
        const transactionOptionsList =
          data?.transaction_type?.map((el) => ({
            value: el?.id,
            label: el?.name,
          })) || [];

        switch (listType) {
          case "usersList&role_id=3":
            setBdsOptions(userOptions);
            break;
          case "transaction_type":
            setTransactionOptions(transactionOptionsList);
            break;
        }
      })
      .catch((err) => {
        if (
          err.response?.data?.message &&
          err.response?.data?.message !== "Too Many Attempts."
        ) {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  const fetchLinks = (inputValue = "") => {
    if (!contactId) return;
    setLoadingLinks(true);
    let url = `${BASE_URL}/get-ContactData/${contactId}`;
    if (inputValue.trim() !== "") {
      url += `?search=${encodeURIComponent(inputValue.trim())}`;
    }

    axios
      .get(url, config)
      .then((res) => {
        const data = res?.data?.data;
        const properties = data?.properties;
        const acquisitions = data?.acquisitions;
        const portfolios = data?.portfolios;

        const propertyOptions = properties?.map((el) => ({
          value: el?.id,
          label: el?.property_name,
          desc: `${el.street_address}, ${el.city}, ${el.state}`,
        }));

        const acquisitionOptions = acquisitions?.map((el) => ({
          value: el?.id,
          label: el?.property_type?.[0]?.type,
          minPrice: el?.min_price,
          maxPrice: el?.max_price,
        }));

        const portfolioOptions = portfolios?.map((el) => ({
          value: el?.id,
          label: el?.name,
          desc: ``,
        }));

        if (formData.lead_type === "acquisition" && formData.portfolio !== 1) {
          setLinkOptions(acquisitionOptions || []);
        } else if (formData.lead_type === "disposition" && formData.portfolio !== 1) {
          setLinkOptions(propertyOptions || []);
        } else {
          setLinkOptions(portfolioOptions || []);
        }
      })
      .catch((err) => {
        if (
          err.response?.data?.message &&
          err.response?.data?.message !== "Too Many Attempts."
        ) {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoadingLinks(false));
  };

  useEffect(() => {
    if (showModal) {
      fetchUsers("usersList&role_id=3");
      fetchUsers("transaction_type");
    }
  }, [showModal]);

  useEffect(() => {
    if (showModal && contactId) {
      fetchLinks();
    }
  }, [showModal, contactId, formData.lead_type, formData.portfolio]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChange = (value, name) => {
    setFormData((prev) => ({ ...prev, [name]: value || "" }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleLeadTypeChange = (value) => {
    setFormData({ ...formData, lead_type: value, link: "" });
    setErrors({ ...errors, lead_type: "" });
  };

  const handlePortfolioChange = () => {
    const portfolioStatus = formData.portfolio ? 0 : 1;
    setFormData({ ...formData, portfolio: portfolioStatus, link: "" });
    setErrors({ ...errors, lead_type: "", link: "" });
  };

  const [disable, setDisable] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let newErrors = {};
    if (!formData.associate) {
      newErrors.associate = "Associate is required.";
    }
    if (formData.portfolio !== 1 && !formData.lead_type) {
      newErrors.lead_type = "Transaction Type is required.";
    }
    if (!formData.link) {
      newErrors.link = "Property/Acquisition Criteria is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setDisable(true);

    const dataToSend = {
      contact_id: contactId,
      date_added: formData.date_added,
      associate_id: formData.associate,
      transaction_type: formData.lead_type === "disposition" ? 0 : (formData.lead_type === "acquisition" ? 1 : formData.lead_type),
      portfolio: formData.portfolio,
      link_id: formData.link,
      stage: 8,
    };

    axios
      .post(`${BASE_URL}/add-pipeline`, dataToSend, config)
      .then((res) => {
        onClose();
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisable(false));
  };

  const formatOptionLabel = ({ label, minPrice, maxPrice, desc }) => (
    <div>
      <div>{label}</div>
      {desc && <p className="dark-M">{desc}</p>}
      {(minPrice || maxPrice) && (
        <div className="flex gap-2 dark-M">
          {minPrice && <div>Min Price-{minPrice}</div>}
          {minPrice && maxPrice && "/"}
          {maxPrice && <div>Max Price-{maxPrice}</div>}
        </div>
      )}
    </div>
  );

  return (
    <Modal title={"Add to Farm Club"} desc={"Provide details to add this contact to the Farm Club."} width="550px" show={showModal} onClose={onClose}>
      <form className="mt-4" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4">
          <div className="w-full">
            <label className="dark-H head-6 block mb-1">Date Added</label>
            <div className="w-full">
              <DatePicker
                className="body-N w-full"
                dateFormat="MM/dd/yyyy"
                placeholderText="MM/DD/YYYY"
                selected={formData.date_added ? new Date(formData.date_added) : null}
                onChange={(date) => handleChange(date, "date_added")}
              />
            </div>
          </div>

          <div>
            <label className="dark-H head-6 block mb-1">
              Associate <span className="text-red-500">*</span>
            </label>
            <Select
              ref={selectRef}
              className="body-N"
              options={bdsOptions}
              placeholder="Select Associate"
              value={
                bdsOptions.find(
                  (option) => option?.value === formData?.associate
                ) || null
              }
              onChange={(option) => handleChange(option?.value, "associate")}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 999999 })
              }}
              menuPortalTarget={document.body}
            />
            {errors.associate && <p className="body-S red-D mt-1">{errors.associate}</p>}
          </div>

          <div>
            <label className="dark-H head-6 block mb-1">
              Transaction Type {formData.portfolio !== 1 && <span className="text-red-500">*</span>}
            </label>
            <div className="mt-2">
              <RadioGroup
                onChange={handleLeadTypeChange}
                value={formData?.lead_type}
              >
                <Stack direction="row" gap={5}>
                  <Radio value="disposition">Disposition</Radio>
                  <Radio value="acquisition">Acquisition</Radio>
                </Stack>
              </RadioGroup>
            </div>
            {errors.lead_type && <p className="body-S red-D mt-1">{errors.lead_type}</p>}
          </div>

          <div>
            <label className="container" style={{ marginBottom: "0px" }}>
              <p style={{ paddingLeft: "30px" }} className="dark-H head-6">Portfolio</p>
              <input
                type="checkbox"
                checked={formData?.portfolio === 1}
                className="body-N"
                onChange={handlePortfolioChange}
              />
              <span className="checkmark"></span>
            </label>
          </div>

          <div>
            <label className="dark-H head-6 block mb-1">
              Property/Acquisition Criteria <span className="text-red-500">*</span>
            </label>
            <Select
              className="body-N"
              options={linkOptions}
              placeholder="Select"
              value={linkOptions.find((option) => option.value === formData.link) || null}
              onChange={(option) => {
                handleChange(option ? option.value : null, "link");
                setErrors({ ...errors, link: "" });
              }}
              formatOptionLabel={formatOptionLabel}
              isSearchable={true}
              isClearable={true}
              loading={loadingLinks}
              isDisabled={formData?.lead_type === "" && formData?.portfolio !== 1}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 999999 })
              }}
              menuPortalTarget={document.body}
            />
            {errors.link && <p className="body-S red-D mt-1">{errors.link}</p>}
            {linkOptions?.length === 0 && formData?.lead_type !== "" && (
              <p className="body-S red-D mt-1">
                No {formData?.lead_type === "disposition" ? "property" : "acquisition criteria"} found under this contact.{" "}
              </p>
            )}
          </div>
        </div>

        <hr className="mt-6" />

        <div className="mt-6">
          <button type="submit" disabled={disable} className="save-button light-L head-5 green-bg-H">
            {disable ? "Saving..." : "Save"}
          </button>
          <button type="button" onClick={onClose} disabled={disable} className="green-H ml-5">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddToFarmClubModal;
